import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import PageHero, { Breadcrumb } from "@/components/feature/PageHero";
import TitleCard from "@/components/feature/TitleCard";
import { useSeo } from "@/hooks/useSeo";
import { sortOptions, titles, type MediaType, type SortValue } from "@/mocks/titles";
import FilterPanel from "@/pages/category/components/FilterPanel";

const PAGE_SIZE = 8;

function matchYear(year: number, option: string) {
  if (option === "全部年份") return true;
  if (option === "2022及更早") return year <= 2022;
  return String(year) === option;
}

export default function Category() {
  const [params, setParams] = useSearchParams();
  const type = (params.get("type") === "tv" ? "tv" : params.get("type") === "movie" ? "movie" : "all") as
    | MediaType
    | "all";

  const [genre, setGenre] = useState("全部题材");
  const [region, setRegion] = useState("全部地区");
  const [year, setYear] = useState("全部年份");
  const [sort, setSort] = useState<SortValue>("hot");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filterOpen, setFilterOpen] = useState(false);

  const isMovie = type === "movie";
  const isTv = type === "tv";
  const pageTitle = isMovie ? "电影推荐" : isTv ? "电视剧热播" : "全部影视作品";

  useSeo({
    title: isMovie
      ? "星光传媒电影分类 - 发现优质影视作品"
      : isTv
        ? "星光传媒电视剧分类 - 热播剧集一网打尽"
        : "星光传媒影视分类 - 发现优质影视作品",
    description: isMovie
      ? "星光传媒电影分类页汇聚院线新片、影展佳作与经典电影，支持按题材、地区、年份筛选与评分排序，帮你快速找到值得一看的电影。"
      : isTv
        ? "星光传媒电视剧分类页覆盖热播剧集与口碑剧集，可按题材、地区、年份筛选并排序，随时跟进连载更新与完结剧集。"
        : "星光传媒影视分类页聚合电影与电视剧作品，支持题材、地区、年份组合筛选和热门、最新、评分排序。",
    keywords: "星光传媒,电影分类,电视剧分类,影视作品筛选,电影推荐,剧集推荐",
    canonicalPath: `/category${isMovie ? "?type=movie" : isTv ? "?type=tv" : ""}`,
  });

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [genre, region, year, sort, type]);

  const filtered = titles
    .filter((item) => (type === "all" ? true : item.type === type))
    .filter((item) => (genre === "全部题材" ? true : item.genres.includes(genre)))
    .filter((item) => (region === "全部地区" ? true : item.region === region))
    .filter((item) => matchYear(item.year, year))
    .sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "new") return b.year - a.year || b.hot - a.hot;
      return b.hot - a.hot;
    });

  const shown = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  const resetFilters = () => {
    setGenre("全部题材");
    setRegion("全部地区");
    setYear("全部年份");
    setSort("hot");
    setVisible(PAGE_SIZE);
  };

  const switchType = (next: MediaType | "all") => {
    const nextParams = new URLSearchParams(params);
    if (next === "all") {
      nextParams.delete("type");
    } else {
      nextParams.set("type", next);
    }
    setParams(nextParams, { replace: true });
    resetFilters();
  };

  const filterProps = {
    genre,
    region,
    year,
    sort,
    onGenre: setGenre,
    onRegion: setRegion,
    onYear: setYear,
    onSort: setSort,
    onReset: resetFilters,
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar variant="solid" currentLabel={isMovie ? "电影" : isTv ? "电视剧" : "首页"} />
      <main>
        <PageHero
          eyebrow="Category"
          title={pageTitle}
          description={
            isMovie
              ? "从院线新片到影展佳作，我们把电影按题材、地区与年份整理清楚。评分来自星光编辑部与观众共同打分，仅作观影参考。"
              : isTv
                ? "热播剧集、口碑完结剧与待播新剧集中在这里。支持按题材、地区与年份筛选，跟播不再迷路。"
                : "电影与电视剧全部作品汇总，按题材、地区、年份自由组合筛选，快速找到今晚要看的那一部。"
          }
          background="https://readdy.ai/api/search-image?query=Cinematic%20dark%20movie%20theatre%20auditorium%20from%20behind%20with%20warm%20amber%20screen%20glow%20and%20deep%20navy%20seats%2C%20moody%20atmospheric%20minimal%20composition%2C%20high%20detail&width=1600&height=900&seq=category-hero-bg&orientation=landscape"
        >
          <div className="mt-5 md:mt-6 flex flex-wrap gap-2">
            {[
              { label: "全部作品", value: "all" as const },
              { label: "电影推荐", value: "movie" as const },
              { label: "电视剧热播", value: "tv" as const },
            ].map((tab) => (
              <button
                key={tab.value}
                type="button"
                onClick={() => switchType(tab.value)}
                className={`h-9 px-4 rounded-full text-xs md:text-sm transition-colors cursor-pointer whitespace-nowrap ${
                  type === tab.value
                    ? "bg-accent-500 text-primary-950"
                    : "border border-background-50/25 text-background-100 hover:bg-background-50/10"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
        </PageHero>

        <div className="px-4 md:px-6 py-5 border-b border-background-200 bg-background-50">
          <div className="max-w-[1440px] mx-auto">
            <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "影视分类" }, { label: pageTitle }]} />
          </div>
        </div>

        <section className="px-4 md:px-6 py-6 md:py-8">
          <div className="max-w-[1440px] mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6 md:gap-8">
              <aside className="hidden lg:block">
                <div className="sticky top-24 bg-background-100 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h2 className="text-sm font-medium text-foreground-950">筛选条件</h2>
                    <button
                      type="button"
                      onClick={resetFilters}
                      className="text-xs text-primary-700 hover:text-primary-800 cursor-pointer"
                    >
                      重置
                    </button>
                  </div>
                  <FilterPanel {...filterProps} layout="vertical" />
                </div>
              </aside>

              <div>
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                  <p className="text-sm text-foreground-600">
                    共 <span className="text-primary-700 font-medium">{filtered.length}</span> 部作品
                    <span className="hidden sm:inline text-foreground-400"> · 演示数据，持续更新中</span>
                  </p>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setFilterOpen(true)}
                      className="lg:hidden inline-flex items-center gap-1.5 h-9 px-3 rounded-md border border-background-300 text-xs text-foreground-700 cursor-pointer whitespace-nowrap"
                    >
                      <i className="ri-filter-3-line w-4 h-4 flex items-center justify-center"></i>
                      筛选
                    </button>
                    <div className="flex items-center gap-1 bg-background-100 rounded-full p-1">
                      {sortOptions.map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setSort(option.value)}
                          className={`px-3 py-1.5 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                            sort === option.value
                              ? "bg-background-50 text-primary-700 font-medium"
                              : "text-foreground-600 hover:text-primary-700"
                          }`}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {[
                    { label: genre, active: genre !== "全部题材", onClear: () => setGenre("全部题材") },
                    { label: region, active: region !== "全部地区", onClear: () => setRegion("全部地区") },
                    { label: year, active: year !== "全部年份", onClear: () => setYear("全部年份") },
                  ]
                    .filter((chip) => chip.active)
                    .map((chip) => (
                      <button
                        key={chip.label}
                        type="button"
                        onClick={chip.onClear}
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-secondary-100 text-xs text-secondary-900 cursor-pointer whitespace-nowrap"
                      >
                        {chip.label}
                        <i className="ri-close-line w-3.5 h-3.5 flex items-center justify-center"></i>
                      </button>
                    ))}
                </div>

                {shown.length > 0 ? (
                  <>
                    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3 md:gap-5">
                      {shown.map((item) => (
                        <TitleCard key={item.id} title={item} />
                      ))}
                    </div>
                    <div className="mt-8 flex flex-col items-center gap-3">
                      {hasMore ? (
                        <button
                          type="button"
                          onClick={() => setVisible((prev) => prev + PAGE_SIZE)}
                          className="inline-flex items-center gap-2 h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          加载更多作品
                          <span className="text-xs text-background-200">
                            {Math.min(visible, filtered.length)} / {filtered.length}
                          </span>
                        </button>
                      ) : (
                        <p className="text-xs text-foreground-400">已经到底了 · 全部 {filtered.length} 部作品已展示</p>
                      )}
                    </div>
                  </>
                ) : (
                  <div className="py-16 md:py-20 text-center bg-background-100 rounded-lg">
                    <span className="inline-flex w-14 h-14 rounded-full bg-background-50 text-foreground-400 items-center justify-center mb-4">
                      <i className="ri-file-search-line text-2xl w-6 h-6 flex items-center justify-center"></i>
                    </span>
                    <h3 className="font-heading text-lg text-foreground-950 mb-2">暂无符合条件的影视作品</h3>
                    <p className="text-sm text-foreground-500 mb-5">试着放宽筛选条件，或者看看全部推荐。</p>
                    <div className="flex flex-wrap items-center justify-center gap-3">
                      <button
                        type="button"
                        onClick={resetFilters}
                        className="h-10 px-5 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                      >
                        重置筛选条件
                      </button>
                      <Link
                        to="/category"
                        className="h-10 px-5 inline-flex items-center rounded-md border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-700 cursor-pointer whitespace-nowrap"
                      >
                        查看全部作品
                      </Link>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />

      {/* Mobile filter sheet */}
      <div
        className={`fixed inset-0 z-[65] lg:hidden transition-opacity duration-300 ${
          filterOpen ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
        }`}
      >
        <div className="absolute inset-0 bg-foreground-950/50" onClick={() => setFilterOpen(false)}></div>
        <div
          className={`absolute bottom-0 left-0 right-0 bg-background-50 rounded-t-2xl max-h-[82vh] flex flex-col transition-transform duration-300 ${
            filterOpen ? "translate-y-0" : "translate-y-full"
          }`}
        >
          <div className="flex items-center justify-between px-4 h-14 border-b border-background-200">
            <h3 className="text-sm font-medium text-foreground-950">筛选条件</h3>
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              aria-label="关闭筛选"
              className="w-9 h-9 rounded-md flex items-center justify-center text-foreground-600 cursor-pointer"
            >
              <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center"></i>
            </button>
          </div>
          <div className="flex-1 overflow-y-auto px-4">
            <FilterPanel {...filterProps} layout="vertical" />
          </div>
          <div className="p-4 border-t border-background-200 flex gap-3">
            <button
              type="button"
              onClick={resetFilters}
              className="flex-1 h-11 rounded-md border border-background-300 text-sm text-foreground-700 cursor-pointer whitespace-nowrap"
            >
              重置
            </button>
            <button
              type="button"
              onClick={() => setFilterOpen(false)}
              className="flex-1 h-11 rounded-md bg-primary-500 text-background-50 text-sm cursor-pointer whitespace-nowrap"
            >
              查看 {filtered.length} 部作品
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}